import React, { useEffect } from 'react'
import { Card, CardContent, Box, Tab } from '@mui/material'
import LanguageStrings from '../../../../i18n/locales'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import './AddDeviceParameters.css'
import { connect, type ConnectedProps } from 'react-redux'
import { fetchPerformanceIndicators, fetchDeviceParamsConfig, fetchFrequencyMetaData } from '../../redux/actionCreators'
import { type indicatorsState } from '../../redux/indicatorSlice'
import { type parametersState } from '../../redux/modbusParamsSlice'
import { type FrequencyMetaDataState } from '../../redux/frequencyMetaDataSlice'
import { type RootState } from '../../../../store'
import PerformanceIndicators from './PerformanceIndicators'
import ModbusParameters from './ModbusParameters'
import { type Parameter } from '../interface'

const AddDeviceStrings = LanguageStrings().DeviceManagementCollectionStrings.AddDeviceComponent

const cardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px'
}
const tabStyles = {
  color: 'gray',
  fontSize: '16px',
  fontFamily: 'Honeywell Sans Web',
  fontWeight: '500',
  textTransform: 'unset'
}

interface Indicator {
  id: string
  kpiDefinitionId: string
  kpiName: string
  assetId: string | null
  isKpiEnabled: boolean
  isAlarmApplicable: boolean
  isAlarmEnabled: boolean
  alarmHigherLimitSeverity: string | null
  alarmLowerLimitSeverity: string | null
  isCustomKpi: boolean
  tenantId: string | null
  unit: string | null
  isDefaultConfig: boolean
  executionFrequencyMinutes: number | null
  alarmLowerLimit: number | null
  alarmLowerLimitStateText: string | null
  alarmLowerLimitTextColorId: string | null
  alarmLowerLimitColorName: string | null
  alarmLowerLimitColorValue: string | null
  alarmLowerLimitSeverityId: string | null
  alarmLowerLimitSeverityLevel: string | null
  alarmLowerLimitLabelId: string | null
  alarmLowerLimitLabelName: string | null
  alarmLowerLimitRecommendationText: string | null
  alarmHigherLimit: string | null
  alarmHigherLimitStateText: string | null
  alarmHigherLimitTextColorId: string | null
  alarmHigherLimitColorName: string | null
  alarmHigherLimitColorValue: string | null
  alarmHigherLimitSeverityId: string | null
  alarmHigherLimitSeverityLevel: string | null
  alarmHigherLimitLabelId: string | null
  alarmHigherLimitLabelName: string | null
  alarmHigherLimitRecommendationText: string | null
  isTotalizer?: boolean
}

interface ParentProps {
  assetId: string
  assetTypeId: string
  setIsStdError: React.Dispatch<React.SetStateAction<any>>
  setIsCustomError: React.Dispatch<React.SetStateAction<any>>
  setIsModbusError: React.Dispatch<React.SetStateAction<any>>
  setIndicators: React.Dispatch<React.SetStateAction<any>>
  setParameters: React.Dispatch<React.SetStateAction<any>>
  indicators: Indicator[] | []
  parameters: Parameter[] | []
}

const AddDeviceParameters = (props: PropsFromRedux & ParentProps): JSX.Element => {
  const isCustom = sessionStorage.getItem('isCustomDevice') !== null ? sessionStorage.getItem('isCustomDevice') : 'false'
  const [value, setValue] = React.useState(isCustom === 'true' ? '2' : '1')
  const [indicators, setIndicators] = React.useState<any>(props.indicators)
  const [parameters, setParameters] = React.useState<any>(props.parameters)

  useEffect(() => {
    // istanbul ignore else
    if (props.indicators.length === 0 && props.assetId !== null) {
      props.fetchPerformanceIndicators(props.assetId, props.assetTypeId)
    }
    // istanbul ignore else
    if (props.parameters.length === 0 && props.assetId !== null) {
      props.fetchDeviceParamsConfig(props.assetId, props.assetTypeId)
    }
    // Fetch frequency metadata on component mount
    props.fetchFrequencyMetaData()
  }, [props.assetId])

  useEffect(() => {
    // istanbul ignore else
    if (
      props.indicators.length === 0 &&
      (props.performanceIndicator?.indicators != null &&
      props.performanceIndicator?.indicators.length > 0)
    ) {
      const indicatorrArr = props.performanceIndicator.indicators.map(item => {
        const { isTotalizer, ...rest } = item as Indicator
        const isTotalizerExist = isTotalizer ?? false
        return {
          ...rest,
          isTotalizer,
          executionFrequencyMinutes: isTotalizerExist ? 1 : item.executionFrequencyMinutes
        }
      })
      setIndicators(indicatorrArr)
      props.setIndicators(indicatorrArr)
    }
  }, [props.performanceIndicator])

  useEffect(() => {
    // istanbul ignore else
    if (
      props.parameters.length === 0 &&
      (props.modbusParameters?.modbusParameters != null &&
      props.modbusParameters?.modbusParameters.length > 0)
    ) {
      setParameters(props.modbusParameters.modbusParameters)
      props.setParameters(props.modbusParameters.modbusParameters)
    }
  }, [props.modbusParameters])

  const handleChange = (event: React.SyntheticEvent, newValue: string): any => {
    setValue(newValue)
  }
  const handleIndicators = (indicatorList: Indicator[]): void => {
    props.setIndicators(indicatorList)
    setIndicators(indicatorList)
  }
  const handleModbusParams = (parameterList: Parameter[]): void => {
    props.setParameters(parameterList)
    setParameters(parameterList)
  }

  return (
    <div className="device-parameters">
      <Card sx={cardStyles}>
        <CardContent>
          <div className="parameter-content">
            <p className="parameter-heading">
              {AddDeviceStrings.AddParameters}
            </p>
          </div>
          <Box sx={{ marginTop: '16px', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
            <TabContext value={value}>
                <TabList data-testid='device-parameters-tabs' sx={{ background: '#202020', justifyContent: 'center' }} style={ isCustom === 'true' ? { width: 'max-content' } : { width: '475px' } } onChange={handleChange} aria-label="lab API tabs example">
                <Tab sx={tabStyles} value="1" label={AddDeviceStrings.StandardKPIs} style={ isCustom === 'true' ? { display: 'none' } : {} } disabled={isCustom === 'true'} />
                <Tab sx={tabStyles} value="2" label={AddDeviceStrings.CustomKPIs}/>
                <Tab sx={tabStyles} value="3" label={AddDeviceStrings.OtherParameters} />
                </TabList>
              <TabPanel value="1" sx={{ width: { xs: '100%', lg: '100%', xl: '70%' }, maxWidth: '100%' }}>
                <PerformanceIndicators setIsError={props.setIsStdError} setIndicators={handleIndicators} assetId={props.assetId} assetTypeId={props.assetTypeId} indicators={indicators} instanceType="standard" />
              </TabPanel>
              <TabPanel value="2" sx={{ width: { xs: '100%', lg: '100%', xl: '70%' } }} >
                <PerformanceIndicators setIsError={props.setIsCustomError} setIndicators={handleIndicators} assetId={props.assetId} assetTypeId={props.assetTypeId} indicators={indicators} instanceType="custom" />
              </TabPanel>
              <TabPanel value="3" sx={{ width: { xs: '100%', lg: '100%', xl: '70%' } }} >
                <ModbusParameters setIsModbusError={props.setIsModbusError} setParameters={handleModbusParams} assetId={props.assetId} assetTypeId={props.assetTypeId} parameters={parameters} />
              </TabPanel>
            </TabContext>
          </Box>
        </CardContent>
      </Card>
    </div>
  )
}

interface StateToProps {
  performanceIndicator: indicatorsState
  modbusParameters: parametersState
  frequencyMetaDataState: FrequencyMetaDataState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  performanceIndicator: state.performanceIndicator,
  modbusParameters: state.modbusParameters,
  frequencyMetaDataState: state.frequencyMetaDataState
})

interface DispatchToProps {
  fetchPerformanceIndicators: (assetId: string, assetTypeId: string) => void
  fetchDeviceParamsConfig: (assetId: string, assetTypeId: string) => void
  fetchFrequencyMetaData: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchPerformanceIndicators: (assetId: string, assetTypeId: string) => dispatch(fetchPerformanceIndicators(assetId, assetTypeId)),
  fetchDeviceParamsConfig: (assetId: string, assetTypeId: string) => dispatch(fetchDeviceParamsConfig(assetId, assetTypeId)),
  fetchFrequencyMetaData: () => dispatch(fetchFrequencyMetaData())
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AddDeviceParameters)
